import { motion } from "framer-motion";
import { Sparkles } from "lucide-react";
import { useT } from "../i18n/LanguageContext";

export default function Combo() {
    const { t } = useT();
    return (
        <section
            id="combo"
            className="relative py-24 md:py-32 bg-charcoal"
            data-testid="combo-section"
        >
            <div className="mx-auto max-w-7xl px-6 md:px-12">
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-16 items-stretch">
                    {/* Image */}
                    <motion.div
                        initial={{ opacity: 0, x: -30 }}
                        whileInView={{ opacity: 1, x: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.8 }}
                        className="relative lg:col-span-7 aspect-[4/3] lg:aspect-auto overflow-hidden border border-white/5"
                    >
                        <img
                            src="https://customer-assets.emergentagent.com/job_wynwood-short-term/artifacts/g22a06j4_Imagen%2021-4-26%20a%20las%2021.21%20%281%29.jpeg"
                            alt="Private office suite with Miami skyline view"
                            className="w-full h-full object-cover"
                        />
                        <div className="absolute inset-0 bg-gradient-to-tr from-charcoal/70 via-charcoal/10 to-transparent" />
                        <div className="absolute bottom-6 left-6 right-6 flex flex-wrap gap-3">
                            <span className="px-4 py-2 bg-charcoal/80 backdrop-blur border border-white/10 text-[11px] tracking-[0.25em] uppercase text-white">
                                {t("combo.badge_res")}
                            </span>
                            <span className="px-4 py-2 bg-gold text-charcoal text-[11px] tracking-[0.25em] uppercase font-semibold">
                                {t("combo.badge_office")}
                            </span>
                        </div>
                    </motion.div>

                    {/* Text */}
                    <motion.div
                        initial={{ opacity: 0, x: 30 }}
                        whileInView={{ opacity: 1, x: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.8, delay: 0.1 }}
                        className="lg:col-span-5 flex flex-col justify-center"
                    >
                        <div className="flex items-center gap-3 mb-6">
                            <span className="inline-block w-8 h-[1px] bg-gold" />
                            <span className="text-[11px] tracking-[0.4em] uppercase text-gold font-semibold">
                                {t("combo.overline")}
                            </span>
                        </div>
                        <h2 className="font-display text-3xl md:text-4xl lg:text-5xl font-semibold tracking-tight leading-[1.05]">
                            {t("combo.heading_line1")}
                            <br />
                            <span className="italic font-normal text-gold">
                                {t("combo.heading_highlight")}
                            </span>
                        </h2>
                        <p className="mt-6 text-white/70 text-base md:text-lg leading-relaxed">
                            {t("combo.body")}
                        </p>

                        {/* Fully furnished emphasis */}
                        <div
                            className="mt-6 flex items-start gap-3 p-4 border border-gold/40 bg-gold/5"
                            data-testid="combo-furnished-note"
                        >
                            <span className="mt-0.5 inline-flex items-center gap-2 px-2 py-1 bg-gold text-charcoal text-[10px] tracking-[0.2em] uppercase font-bold whitespace-nowrap">
                                <Sparkles size={11} strokeWidth={2.5} />
                                {t("combo.furnished_badge")}
                            </span>
                            <p className="text-sm text-white/85 leading-relaxed">
                                {t("combo.furnished_note")}
                            </p>
                        </div>

                        <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 gap-5">
                            <div className="p-6 border border-white/10 bg-charcoal-surface hover:border-gold/40 transition-colors">
                                <div className="text-[11px] tracking-[0.3em] uppercase text-gold mb-3">
                                    {t("combo.opt_label")} 01
                                </div>
                                <div className="font-display text-lg font-medium leading-snug">
                                    {t("combo.opt1_title")}
                                </div>
                            </div>
                            <div className="p-6 border border-white/10 bg-charcoal-surface hover:border-gold/40 transition-colors">
                                <div className="text-[11px] tracking-[0.3em] uppercase text-gold mb-3">
                                    {t("combo.opt_label")} 02
                                </div>
                                <div className="font-display text-lg font-medium leading-snug">
                                    {t("combo.opt2_title")}
                                </div>
                            </div>
                        </div>
                    </motion.div>
                </div>
            </div>
        </section>
    );
}
